import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  makeStyles,
  Box,
} from "@material-ui/core";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ButtonWithSpinner from "../ButtonWithSpinner";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 700,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    paddingTop: 8,
  },
  infoBox: {
    backgroundColor: theme.palette.mode === "light" ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 255, 255, 0.05)",
    padding: "12px 16px",
    borderRadius: 8,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  confirmButton: {
    backgroundColor: "#2e7d32",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#1b5e20",
    },
  },
}));

const TakeOverTicketModal = ({ modalOpen, onClose, ticket, onTakeOverSuccess }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [messageText, setMessageText] = useState("");

  const currentAttendant = ticket?.user?.name || "outro atendente";

  useEffect(() => {
    if (modalOpen) {
      setMessageText(
        `{{ms}} *{{name}}*, meu nome é *${user?.name || ""}* e agora vou dar continuidade ao seu atendimento!`
      );
      setNotifyCustomer(true);
    }
  }, [modalOpen, user?.name]);

  const handleTakeOver = async () => {
    if (!ticket?.id) return;
    setLoading(true);

    try {
      // 1. Atualizar o atendente do ticket para o usuário logado
      await api.put(`/tickets/${ticket.id}`, {
        status: "open",
        userId: user?.id,
        useIntegration: false,
        promptId: null,
        integrationId: null,
      });

      // 2. Se marcado para notificar o cliente, enviar a mensagem de continuidade
      if (notifyCustomer && messageText.trim() && !ticket.isGroup) {
        const messagePayload = {
          read: 1,
          fromMe: true,
          mediaUrl: "",
          body: `*Mensagem Automática:*\n${messageText.trim()}`,
        };
        await api.post(`/messages/${ticket.id}`, messagePayload);
      }

      toast.success("Atendimento assumido com sucesso!");

      if (typeof onTakeOverSuccess === "function") {
        onTakeOverSuccess();
      }

      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={modalOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gridGap={8}>
          <AssignmentIndIcon color="primary" />
          <span>Assumir Atendimento</span>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <div className={classes.infoBox}>
          <Typography variant="body1">
            Este atendimento está atualmente com <strong>{currentAttendant}</strong>.
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
            Ao assumir, o chamado será transferido para você (<strong>{user?.name}</strong>).
          </Typography>
        </div>

        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={notifyCustomer}
              onChange={(e) => setNotifyCustomer(e.target.checked)}
            />
          }
          label="Notificar cliente sobre a continuidade do atendimento"
        />

        {notifyCustomer && (
          <TextField
            label="Mensagem de Continuidade"
            multiline
            rows={3}
            variant="outlined"
            fullWidth
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            helperText="Variáveis: {{ms}} (Bom dia/Boa tarde/Boa noite), {{name}} (Nome do cliente)"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default" disabled={loading}>
          Cancelar
        </Button>
        <ButtonWithSpinner
          className={classes.confirmButton}
          variant="contained"
          onClick={handleTakeOver}
          loading={loading}
          startIcon={<AssignmentIndIcon />}
        >
          Assumir Atendimento
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default TakeOverTicketModal;
